
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import ScrollAnimation from '../ScrollAnimation';

const HeroContent = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  return (
    <div className="md:w-1/2 space-y-6 overflow-hidden">
      <ScrollAnimation className="stagger-1">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="inline-flex items-center px-3.5 py-1.5 bg-primary/8 text-primary text-sm font-medium rounded-full border border-primary/15 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2 animate-pulse" />
            {t('hero.tag')}
          </span>
          <span className="text-xs text-success font-medium bg-success/8 px-2.5 py-1 rounded-full border border-success/15">
            {t('hero.aiPowered')}
          </span>
        </div>
      </ScrollAnimation>
      
      <ScrollAnimation className="stagger-2">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground overflow-wrap-break-word" style={{ lineHeight: '1.1', letterSpacing: '-0.03em', overflowWrap: 'break-word' }}>
          {t('hero.title')}
          <span className="block text-primary mt-1">{t('hero.financialIntelligence')}</span>
        </h1>
      </ScrollAnimation>
      
      <ScrollAnimation className="stagger-3">
        <p className="text-base md:text-lg text-muted-foreground max-w-lg" style={{ textWrap: 'pretty', overflowWrap: 'break-word' }}>
          {t('hero.description')}
          <span className="block mt-2 text-foreground/70 font-medium text-base">
            {t('hero.additionalDescription')}
          </span>
        </p>
      </ScrollAnimation>
      
      <ScrollAnimation className="stagger-3">
        <div className="flex items-center gap-5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-success rounded-full" />
            <span>{t('hero.enterpriseSecurity')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-success rounded-full" />
            <span>{t('hero.realTimeAnalysis')}</span>
          </div>
        </div>
      </ScrollAnimation>
      
      <ScrollAnimation className="stagger-4">
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-7 py-3 text-base font-medium group active:scale-[0.97] transition-all duration-200"
            onClick={() => navigate('/dashboard')}
            aria-label="Get started with DCFlow dashboard"
          >
            {t('hero.getStarted')}
            <ArrowRight size={17} className="ml-2 group-hover:translate-x-0.5 transition-transform duration-200" />
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="border-border hover:bg-muted/50 px-7 py-3 text-base active:scale-[0.97] transition-all duration-200"
            aria-label="Learn more about DCFlow features"
          >
            {t('hero.learnMore')}
          </Button>
        </div>
      </ScrollAnimation>
      
      <ScrollAnimation className="stagger-4">
        <div className="grid grid-cols-3 gap-4 pt-6 text-sm text-muted-foreground">
          <div className="flex flex-col">
            <span className="text-xl sm:text-2xl font-bold text-foreground tabular-nums">{t('stats.accuracy.number')}</span>
            <span className="text-xs">{t('stats.accuracy.label')}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xl sm:text-2xl font-bold text-foreground tabular-nums">{t('stats.documents.number')}</span>
            <span className="text-xs">{t('stats.documents.label')}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xl sm:text-2xl font-bold text-foreground tabular-nums">{t('stats.companies.number')}</span>
            <span className="text-xs">{t('stats.companies.label')}</span>
          </div>
        </div>
      </ScrollAnimation>
    </div>
  );
};

export default HeroContent;
